import { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faEllipsisH } from '@fortawesome/pro-solid-svg-icons';
import { formNameSelector } from "../../store/selectors/entities/form";
import { conditionsSelector } from "../../store/selectors/entities/conditions";
import { clientEntriesSelector, sheetClientEntriesSelector } from "../../store/selectors/clientEntries";
import { clientTypeSelector, hideSubmitButtonSelector, interactiveClientSelector, ignoreConditionsSelector, disableRequiredSelector, addNewLoopEntrySelector } from "../../store/selectors/clientSettings";
import { applyValidations } from "../../lib/fields";
import { conditionCheck } from "../../lib/conditions";
import { orderByPosition } from "../../lib/utils";
import { addNewLoopEntry, touchAllFields } from "../../store/actions/client";
import { ScreenPreview, LoopScreenPreview } from "../screen";
import { ProgressBar } from "./ProgressBar";
import { clearTempStorage } from "../../lib/customHooks/useTempStorage";
const previewFilter = entry => {
  const gpsFilter = entry => {
    return entry.visible !== false;
  };
  switch (entry.type) {
    case 'GPS':
      return gpsFilter(entry);
    default:
      return true;
  }
};
const checkEntryConditionsAndValidations = ({
  entries,
  clientEntries,
  conditions,
  interactive = true,
  disableRequired = false,
  ignoreConditions = false
}) => entries.map(entry => {
  const entryId = entry.parentGuid || entry.guid;
  const conditionMet = !ignoreConditions && interactive ? conditionCheck(entryId, clientEntries, conditions, entry.rowGuid) : true;
  const validatedEntry = interactive && conditionMet ? applyValidations(entry, {
    disableRequired
  }) : {
    ...entry,
    isValid: true
  };
  return {
    conditionMet,
    ...validatedEntry
  };
}).sort(orderByPosition);

// used to .find any invalid entries on a Screen (& that match activeRowGuid if its active)
// findInvalidFields :: (String||'') -> Entry -> (Entry||null)
const findInvalidFields = activeRowGuid => ({
  isValid,
  rowGuid
}) => isValid === false && (!activeRowGuid || rowGuid == activeRowGuid);
export const ClientScreen = ({
  sheet,
  entries
}) => {
  const conditions = useSelector(conditionsSelector);
  const interactive = useSelector(interactiveClientSelector);
  const disableRequired = useSelector(disableRequiredSelector);
  const ignoreConditions = useSelector(ignoreConditionsSelector);
  const {
    id,
    sheet_type_id,
    multi_section,
    subSheets = [],
    description
  } = sheet;
  const allSheetIds = [id, ...subSheets];
  const allSheetEntries = useSelector(state => sheetClientEntriesSelector(state, allSheetIds));
  const clientEntries = useSelector(clientEntriesSelector);
  const screenEntries = entries ? entries : checkEntryConditionsAndValidations({
    entries: allSheetEntries,
    clientEntries,
    interactive,
    conditions,
    disableRequired,
    ignoreConditions
  });
  const isLoopScreen = multi_section && (sheet_type_id == 13 || sheet_type_id == 15) || false;
  return isLoopScreen ? <LoopScreenPreview description={description} sheet={sheet} /> : <ScreenPreview description={description} entries={screenEntries.filter(previewFilter)} sheetType={sheet_type_id} />;
};
export const Client = ({
  onSubmit,
  setSheet,
  sheets,
  activeSheetIndex,
  reset = () => {}
}) => {
  const dispatch = useDispatch();
  const conditions = useSelector(conditionsSelector);
  const interactive = useSelector(interactiveClientSelector);
  const appName = useSelector(formNameSelector);
  const clientType = useSelector(clientTypeSelector);
  const hideSubmitButton = useSelector(hideSubmitButtonSelector);
  const activeRowGuid = useSelector(addNewLoopEntrySelector);
  const activeSheet = sheets[activeSheetIndex] || {
    description: 'No Screen Selected'
  };
  const {
    id,
    subSheets = [],
    description: screenName
  } = activeSheet;
  const allSheetIds = [id, ...subSheets];
  const allSheetEntries = useSelector(state => sheetClientEntriesSelector(state, allSheetIds));
  const clientEntries = useSelector(clientEntriesSelector);
  const screenEntries = checkEntryConditionsAndValidations({
    entries: allSheetEntries,
    clientEntries,
    interactive,
    conditions
  });
  const screenTitleEl = useRef();
  const prev = sheets[activeSheetIndex - 1];
  const next = sheets[activeSheetIndex + 1];
  const percent = (activeSheetIndex + 1) / sheets.length * 100;
  const anySheetErrors = screenEntries.find(findInvalidFields(activeRowGuid));
  const progressBarType = hideSubmitButton ? 'table' : clientType;
  const nextAction = () => {
    if (anySheetErrors) {
      dispatch(touchAllFields({
        sheet_ids: allSheetIds
      }));
    } else if (hideSubmitButton) {
      dispatch(addNewLoopEntry({
        addNewLoopEntry: false,
        activeRowGuid: ''
      }));
    } else if (next) {
      setSheet(next.id);
    } else {
      onSubmit();
      clearTempStorage();
    }
  };
  const prevAction = () => setSheet(prev.id);
  useEffect(() => {
    if (interactive && screenTitleEl && screenTitleEl.current) {
      // Make sure that the client is scrolled to the top of the screen
      screenTitleEl.current.scrollIntoView({
        block: 'end'
      });
    }
  }, [activeSheetIndex]);
  return <>
      <div className="full-preview--content--wrapper">
        <header className={`preview--app--name`}>
          <div className="control--block">
            {prev ? <FontAwesomeIcon className="preview--back" onClick={prevAction} icon={faChevronLeft} /> : ''}
          </div>
          <label>{appName}</label>

          <div className="sheet--controls">
            <div className="control sheet--control">
              <div className="control--icon">
                <FontAwesomeIcon className="sheet--settings" icon={faEllipsisH} role="img" />
              </div>
            </div>
          </div>
        </header>

        <div className="preview--section">
          <div className="preview--content--wrapper">
            <div ref={screenTitleEl} className={`section---name`}>
              {screenName}
            </div>
            <ClientScreen sheet={activeSheet} entries={screenEntries} />
          </div>
        </div>
        <ProgressBar type={progressBarType} nextAction={nextAction} prevAction={prevAction} reset={reset} anySheetErrors={anySheetErrors} percentComplete={percent} atStart={activeSheetIndex === 0} interactive={interactive} />
      </div>
    </>;
};